import { computed, ComputedRef, onBeforeMount, onMounted, Ref, toRefs } from 'vue';
import { useRoute } from '@dh-io-emh/shared/src/composables/router.composable';
import { infuse } from '@dh-io-emh/shared/src/ioc-config';
import { useI18n } from '@dh-io-emh/shared/src/composables/i18n.composable';
import { useProductsStore } from '@dh-io-emh/shared/src/stores/products/products.composable';
import { IDealerAddress } from '@dh-io-emh/shared/src/models/dealer.model';
import { IProductFilterType } from '@dh-io-emh/shared/src/models/product-filter/product-filter.model';

export enum LocationFilterTabs {
  Area,
  Dealer,
}

export interface LocationFilterParams {
  search: string;
  distance: string;
  id: string;
}

export const useLocationsFilter = ({
  filter,
  emit,
}: {
  filter?: Ref<IProductFilterType | undefined> | ComputedRef<IProductFilterType | undefined>;
  emit?: (event: 'change', ...args: any[]) => void;
}) => {
  const route = useRoute();
  const dealerApi = infuse('dealerApi');
  const { t } = useI18n();

  const {
    srp: {
      locationFilter: { displayDealerSearch, displayLocationSearch },
    },
  } = infuse('config');

  const productsStore = useProductsStore();
  const { locationFilterTab, locationFilterParams, dealerFilterInfo } = toRefs(productsStore.state);
  const { setLocationFilterTab, setDealerFilterInfo, setLocationFilterParams } = productsStore.dispatchers;

  // computed
  /**
   * All dealers which pass to the current filter selection
   */
  const allowedList: ComputedRef<string[]> = computed(() =>
    dealers.value.filter((dealer) => dealer.count).map((option) => option.value),
  );

  /**
   * All dealers provided by BE
   */
  const dealers: ComputedRef<any[]> = computed(() => {
    return filter?.value?.options || [];
  });

  const locationSearchHeadline: ComputedRef<string> = computed(() => t('dcp.srp.filters.locationSearch.headline'));

  const dealerSearchHeadline: ComputedRef<string> = computed(() => t('dcp.srp.filters.dealerSearch.headline'));

  const locationFilterTabs = computed(() => {
    const tabs: LocationFilterTabs[] = [];

    if (displayLocationSearch) {
      tabs.push(LocationFilterTabs.Area);
    }

    if (displayDealerSearch) {
      tabs.push(LocationFilterTabs.Dealer);
    }

    return tabs;
  });

  /**
   * Get tracking for currently selected tab
   */
  const trackingValue: ComputedRef<string> = computed(() => {
    if (locationFilterParams?.value) {
      return `${locationFilterParams.value.search}_${locationFilterParams.value.distance
        .replace(/\s/g, '')
        .toLowerCase()}`;
    } else if (dealerFilterInfo?.value) {
      return dealerFilterInfo.value.id;
    } else {
      return '';
    }
  });

  const setInitialLocationFilterTab = () => {
    if (locationFilterTabs.value.length === 1) {
      setLocationFilterTab(locationFilterTabs.value[0]);
    }
  };

  // methods
  /**
   * Emit current location selection to the parent element
   */
  const setFilter = (filterValue: string[]): void => {
    emit &&
      emit('change', {
        filterCode: filter?.value?.code,
        filterValue: filterValue,
        filterType: 'multiSelect',
        filterCurrentlySelectedItemValue: filterValue,
        selected: true,
        trackingValue: trackingValue.value,
      });
  };

  /**
   * Reset location filter and clear location params
   */
  const resetLocationFilter = (): void => {
    resetFilter();
    setLocationFilterParams(undefined);
  };

  /**
   * Reset dealer filter and clear dealer info
   */
  const resetDealerFilter = (): void => {
    resetFilter();
    setDealerFilterInfo(undefined);
  };

  /**
   * Emit empty location selection to the parent element
   */
  const resetFilter = (): void => {
    emit &&
      emit('change', {
        filterCode: filter?.value?.code,
        filterValue: [],
        filterType: 'multiSelect',
        filterCurrentlySelectedItemValue: filter?.value,
        selected: false,
        trackingValue: trackingValue.value,
      });
  };

  /**
   * Vendors are selected in the location-search filter:
   * - set locations filter
   * - set area tab as selected tab
   * - clear the search-dealer filter
   */
  const vendorsSelected = (vendors: string[]): void => {
    setFilter(vendors);
    setLocationFilterTab(LocationFilterTabs.Area);
    setDealerFilterInfo(undefined);
  };

  /**
   * Vendor was selected in the dealer-search filter:
   * - set locations filter
   * - set dealer tab as selected tab
   * - set dealer info in the store
   * - clear the location-search filter
   */
  const vendorSelected = (vendor: IDealerAddress): void => {
    setLocationFilterTab(LocationFilterTabs.Dealer);
    setLocationFilterParams(undefined);
    setDealerFilterInfo({
      id: vendor.baseInfo.outletId,
      name: vendor.baseInfo.name1,
      address: `${vendor.address.line1}, ${vendor.address.zipcode} ${vendor.address.city}`,
    });
    setFilter([vendor.baseInfo.outletId]);
  };

  onBeforeMount(() => {
    setInitialLocationFilterTab();
  });

  /**
   * If page is loaded with a single location in the query,
   * - get the location id
   * - fetch the dealer with corresponding location id
   * - set the vendor
   */
  onMounted(async () => {
    // Checking for existing "search by location"
    if (locationFilterParams?.value?.id) return;

    // Extract dealer id from query
    // Since locations can be a string, an array of strings, null, or undefined
    // we need to check that it represents one single location
    const { locations: locationsQuery } = route.value.query;
    let dealerId: string | undefined;

    if (typeof locationsQuery === 'string') {
      dealerId = locationsQuery;
    } else if (locationsQuery instanceof Array && locationsQuery.length === 1) {
      dealerId = locationsQuery.find((location) => typeof location === 'string') as string | undefined;
    }

    // If there is a dealer id, and if no dealer is present in the filter yet
    // (as in the case of a fresh page load or redirection from product page)
    // fetch the respective dealer
    if (dealerId && !dealerFilterInfo.value) {
      const dealers = await dealerApi.getDealersById(dealerId);
      const dealer = dealers[0];

      if (dealer) {
        vendorSelected({
          baseInfo: {
            outletId: dealer.baseInfo.externalId,
            name1: dealer.baseInfo.name1,
          },
          address: {
            line1: dealer.address.line1,
            zipcode: dealer.address.zipcode,
            city: dealer.address.city,
          },
        } as IDealerAddress);
      }
    }
  });

  return {
    // external state
    locationFilterTab,
    locationFilterParams,
    dealerFilterInfo,

    // actions
    setLocationFilterParams,

    // computed
    allowedList,
    dealers,
    trackingValue,
    locationSearchHeadline,
    dealerSearchHeadline,
    locationFilterTabs,

    // methods
    resetLocationFilter,
    resetDealerFilter,
    vendorsSelected,
    vendorSelected,
  };
};
